// src/features/dashboard/components/EmployeeProductivity/WTDIdleTrendChart.tsx

import {
  ResponsiveContainer,
  ComposedChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  Cell,
  Label,
  Legend,
} from 'recharts';

import {
  useRef,
  useState,
} from 'react';

import {
  WTDIdleTrendResponse,
} from '../../types/Employee/employeeIdleTime.types';

interface Props {
  data: WTDIdleTrendResponse[];
  loading?: boolean;
}

const IDLE_COLOR = '#FF812D';
const TARGET_COLOR = '#E5E7EB';
const THRESHOLD_COLOR = '#EF4444';
const GRID_COLOR = '#ECECEC';

interface HoveredBar {
  type: 'idle' | 'target';
  row: WTDIdleTrendResponse;
  value: number;
  x: number;
  y: number;
}

/* ==========================================================================
 * TARGET LABEL
 * ========================================================================== */

const TargetLabel = (props: any) => {
  const {
    viewBox,
    value,
  } = props;

  if (!viewBox) {
    return null;
  }

  const labelWidth = 72;
  const labelHeight = 20;

  /*
   * Keep the target label completely
   * inside the chart.
   */

  const x =
    viewBox.x +
    viewBox.width -
    labelWidth -
    4;

  const y =
    viewBox.y -
    labelHeight / 2;

  return (
    <g>
      <rect
        x={x}
        y={y}
        width={labelWidth}
        height={labelHeight}
        rx={4}
        fill="#FFF5F5"
        stroke="#FCA5A5"
      />

      <text
        x={
          x +
          labelWidth / 2
        }
        y={y + 13}
        fontSize={10}
        fill="#DC2626"
        textAnchor="middle"
      >
        {value}m target
      </text>
    </g>
  );
};

/* ==========================================================================
 * COMPONENT
 * ========================================================================== */

const WTDIdleTrendChart = ({
  data,
  loading,
}: Props) => {
  const chartRef =
    useRef<HTMLDivElement>(null);

  const [
    hoveredLegend,
    setHoveredLegend,
  ] = useState<string | null>(
    null,
  );

  const [
    hoveredBar,
    setHoveredBar,
  ] = useState<HoveredBar | null>(
    null,
  );

  /* ==========================================================================
   * TARGET
   * ========================================================================== */

  const target =
    Number(
      data?.[0]
        ?.targetMinutes ??
        90,
    );

  /* ==========================================================================
   * SAFE CHART DATA
   *
   * IMPORTANT:
   *
   * Create a new array so that each bar position
   * always belongs to exactly one data row.
   *
   * Do not sort or modify the original API data here.
   * ========================================================================== */

  const chartData =
    Array.isArray(data)
      ? data.map((item) => ({
          ...item,

          idleMinutes:
            Number(item.idleMinutes) || 0,

          targetMinutes:
            Number(
              item.targetMinutes ??
                target,
            ) || 0,
        }))
      : [];

  /* ==========================================================================
   * MAX VALUE
   * ========================================================================== */

  const maxValue =
    Math.max(
      ...chartData.map((d) =>
        Math.max(
          Number(
            d.idleMinutes,
          ),
          Number(
            d.targetMinutes,
          ),
        ),
      ),
      target,
    );

  /* ==========================================================================
   * LOADING
   * ========================================================================== */

  if (loading) {
    return (
      <div
        className="
          h-[320px]
          animate-pulse
          rounded-[18px]
          bg-gray-200
        "
      />
    );
  }

  /* ==========================================================================
   * BAR HOVER
   *
   * IMPORTANT FIX:
   *
   * Do NOT depend only on data[index].
   *
   * Recharts gives the actual bar entry as the first
   * argument of onMouseEnter. Its payload is the exact
   * row belonging to the hovered bar.
   * ========================================================================== */

  const handleBarEnter = (
    type:
      | 'idle'
      | 'target',
    entry: any,
    index: number,
    event: any,
  ) => {
    /*
     * First use the actual Recharts payload.
     *
     * This guarantees that hovering Monday's bar
     * gives Monday's data instead of another index.
     */

    const row =
      entry?.payload ??
      chartData[index];

    if (
      !row ||
      !chartRef.current
    ) {
      return;
    }

    const barRect =
      event?.currentTarget?.getBoundingClientRect();

    const containerRect =
      chartRef.current.getBoundingClientRect();

    if (
      !barRect ||
      !containerRect
    ) {
      return;
    }

    const x =
      barRect.left -
      containerRect.left +
      barRect.width / 2;

    const y =
      barRect.top -
      containerRect.top;

    setHoveredBar({
      type,

      row,

      value:
        type === 'idle'
          ? Number(
              row.idleMinutes,
            ) || 0
          : Number(
              row.targetMinutes ??
                target,
            ) || 0,

      x,

      y,
    });
  };

  const handleBarLeave =
    () => {
      setHoveredBar(null);
    };

  /* ==========================================================================
   * RENDER
   * ========================================================================== */

  return (
    <div
      ref={chartRef}
      className="
        relative
        rounded-[18px]
        border
        border-[#D0D5DD]
        bg-white
        px-6
        pt-5
        pb-4
      "
    >
      {/* ======================================================================
          HEADER
          ====================================================================== */}

      <div
        className="
          mb-4
          flex
          items-center
          gap-3
        "
      >
        <h3
          className="
            text-[15px]
            font-semibold
            text-[#002B5B]
          "
        >
          WTD Idle Time Trend
        </h3>

        <div
          className="
            rounded-full
            bg-[#EEF4FF]
            px-4
            py-[5px]
            text-[12px]
            text-[#2563EB]
          "
        >
          Daily idle minutes vs{' '}
          {target}-min target
        </div>
      </div>

      {/* ======================================================================
          CHART
          ====================================================================== */}

      <div
        className="
          h-[220px]
          w-full
        "
      >
        <ResponsiveContainer
          width="100%"
          height="100%"
        >
          <ComposedChart
            data={chartData}
            margin={{
              top: 10,
              left: -18,
              right: 20,
              bottom: 0,
            }}
            barGap={0}
            barCategoryGap="28%"
          >
            {/* ==================================================================
                GRID
                ================================================================== */}

            <CartesianGrid
              vertical={false}
              stroke={GRID_COLOR}
            />

            {/* ==================================================================
                X AXIS
                ================================================================== */}

            <XAxis
              dataKey="displayDay"
              tick={{
                fontSize: 11,
                fill: '#344054',
              }}
              axisLine={{
                stroke: '#D0D5DD',
              }}
              tickLine={false}
              tickMargin={8}
              interval={0}
              padding={{
                left: 8,
                right: 8,
              }}
            />

            {/* ==================================================================
                Y AXIS
                ================================================================== */}

            <YAxis
              domain={[
                0,
                maxValue + 20,
              ]}
              tickFormatter={(
                value,
              ) =>
                `${value}m`
              }
              tick={{
                fontSize: 11,
              }}
              axisLine={false}
              tickLine={false}
            />

            {/* ==================================================================
                LEGEND
                ================================================================== */}

            <Legend
              verticalAlign="top"
              align="center"
              iconType="square"
              iconSize={16}
              wrapperStyle={{
                paddingBottom: 10,
                cursor: 'pointer',
              }}
              formatter={(value) => (
                <span
                  style={{
                    color:
                      value ===
                      'Idle (min)'
                        ? IDLE_COLOR
                        : '#344054',
                    fontSize:
                      '12px',
                    fontWeight: 500,
                  }}
                >
                  {value}
                </span>
              )}
              onMouseEnter={(
                e: any,
              ) =>
                setHoveredLegend(
                  e.dataKey,
                )
              }
              onMouseLeave={() =>
                setHoveredLegend(null)
              }
            />

            {/* ==================================================================
                TARGET REFERENCE LINE
                ================================================================== */}

            <ReferenceLine
              y={target}
              stroke={
                THRESHOLD_COLOR
              }
              strokeWidth={1.4}
              strokeDasharray="6 6"
            >
              <Label
                position="right"
                value={target}
                content={
                  <TargetLabel />
                }
              />
            </ReferenceLine>

            {/* ==================================================================
                IDLE BAR
                ================================================================== */}

            <Bar
              dataKey="idleMinutes"
              name="Idle (min)"
              legendType="square"
              fill={IDLE_COLOR}
              radius={[
                4,
                4,
                0,
                0,
              ]}
              barSize={18}
              maxBarSize={18}
              onMouseEnter={(
                entry: any,
                index: number,
                event: any,
              ) => {
                handleBarEnter(
                  'idle',
                  entry,
                  index,
                  event,
                );
              }}
              onMouseLeave={
                handleBarLeave
              }
            >
              {chartData.map(
                (row, index) => (
                  <Cell
                    key={`idle-${index}`}
                    fill={
                      IDLE_COLOR
                    }
                    fillOpacity={
                      hoveredLegend ===
                      null
                        ? 1
                        : hoveredLegend ===
                          'idleMinutes'
                          ? 1
                          : 0.25
                    }
                  />
                ),
              )}
            </Bar>

            {/* ==================================================================
                TARGET BAR
                ================================================================== */}

            <Bar
              dataKey="targetMinutes"
              name="Target"
              legendType="square"
              fill={TARGET_COLOR}
              radius={[
                4,
                4,
                0,
                0,
              ]}
              barSize={18}
              maxBarSize={18}
              onMouseEnter={(
                entry: any,
                index: number,
                event: any,
              ) => {
                handleBarEnter(
                  'target',
                  entry,
                  index,
                  event,
                );
              }}
              onMouseLeave={
                handleBarLeave
              }
            >
              {chartData.map(
                (row, index) => (
                  <Cell
                    key={`target-${index}`}
                    fill={
                      TARGET_COLOR
                    }
                    fillOpacity={
                      hoveredLegend ===
                      null
                        ? 1
                        : hoveredLegend ===
                          'targetMinutes'
                          ? 1
                          : 0.25
                    }
                  />
                ),
              )}
            </Bar>
          </ComposedChart>
        </ResponsiveContainer>
      </div>

      {/* ======================================================================
          IDLE / TARGET TOOLTIP
          ====================================================================== */}

      {hoveredBar && (
        <div
          className="
            pointer-events-none
            absolute
            z-[9999]
            min-w-[140px]
            overflow-hidden
            rounded-[6px]
            border
            border-[#E4E7EC]
            bg-white
            shadow-lg
          "
          style={{
            left:
              hoveredBar.x,

            top:
              hoveredBar.y,

            transform:
              'translate(-50%, calc(-100% - 8px))',
          }}
        >
          {/* ==================================================================
              DAY
              ================================================================== */}

          <div
            className="
              border-b
              border-[#EAECF0]
              bg-[#F8F9FB]
              px-3
              py-2
              text-[11px]
              font-medium
              text-[#344054]
            "
          >
            {/*
             * IMPORTANT:
             *
             * Use displayDay because this is exactly
             * the value displayed on the X-axis.
             *
             * This prevents:
             *
             * X-axis  → Monday 24
             * Tooltip → Monday 26
             *
             * mismatch.
             */}

            {hoveredBar.row.displayDay ||
              hoveredBar.row.dayLabel}
          </div>

          {/* ==================================================================
              VALUE
              ================================================================== */}

          <div
            className="
              flex
              items-center
              gap-2
              px-3
              py-2
            "
          >
            <span
              className="
                h-3
                w-3
                shrink-0
                rounded-full
              "
              style={{
                backgroundColor:
                  hoveredBar.type ===
                  'idle'
                    ? IDLE_COLOR
                    : TARGET_COLOR,
              }}
            />

            <span
              className="
                text-[12px]
                text-[#344054]
              "
            >
              {hoveredBar.type ===
              'idle'
                ? 'Idle (min):'
                : 'Target:'}
            </span>

            <span
              className="
                ml-auto
                text-[12px]
                font-semibold
                text-[#101828]
              "
            >
              {hoveredBar.value}m
            </span>
          </div>
        </div>
      )}
    </div>
  );
};

export default WTDIdleTrendChart;